import React, { useState } from "react";

import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  IconButton,
  Stack,
  StackProps,
  Typography
} from "@mui/material";
import { FilesPickerModal } from "components/modals/Files-picker/FilesPicker";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useDealActions } from "store";
import { IFile } from "store/types";

interface Props {
  onClose: () => void;
  dealId: number | null;
  refetchReport: () => void;
}

export const PlaceOnMarketContent: React.FC<Props> = ({ onClose, dealId, refetchReport }) => {
  const { placeOnMarketAction } = useDealActions();

  const [selectedFiles, setSelectedFiles] = useState<{
    attachments: File[];
    files: IFile[];
    listOfNames: string[];
  }>({
    attachments: [],
    files: [],
    listOfNames: []
  });
  const sharedOptions: Record<string, string> = {
    "Latest Capital Account": "latest_capital_account",
    "Financial Statements": "financial_statements",
    "Legal Documents": "legal_documents",
    "Supportive numbers": "supportive_numbers",
    Other: "other"
  };
  const [selectedShareOptions, setSelectedShareOptions] = useState<string[]>([]);
  const [filesPickerModalOpen, setFilesPickerModalOpen] = useState(false);
  const handleToggle = (doc: string) => {
    setSelectedShareOptions((prev) => (prev.includes(doc) ? prev.filter((item) => item !== doc) : [...prev, doc]));
  };

  const filesPickerSubmit = async (attachments: File[], files: IFile[]) => {
    setSelectedFiles({
      attachments,
      files,
      listOfNames: [...attachments.map((file) => file.name), ...files.map((file) => file.name)]
    });
  };
  const removeFile = (file: string) => {
    //remove file from listOfNames also from attachments and files
    const newListOfNames = selectedFiles.listOfNames.filter((item) => item !== file);
    setSelectedFiles({
      ...selectedFiles,
      listOfNames: newListOfNames,
      attachments: selectedFiles.attachments.filter((attachment) => newListOfNames.includes(attachment.name)),
      files: selectedFiles.files.filter((file) => newListOfNames.includes(file.name))
    });
  };

  const handlePlaceOnMarket = async () => {
    if (!dealId) return;
    await placeOnMarketAction({
      file_ids: selectedFiles.files.map((file) => file.id),
      files: selectedFiles.attachments,
      share_option: selectedShareOptions,
      dealId: dealId
    });
    onClose();
    refetchReport();
  };

  return (
    <Stack sx={style}>
      <Stack
        sx={{
          borderBottom: "1px solid  #DAE0E2",
          paddingBottom: "5px",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <Typography sx={{ fontSize: 18, fontWeight: 400, color: "#071F35" }}>Place on market</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon className="icon" />
        </IconButton>
      </Stack>
      <Stack direction="column" sx={{ width: 420, bgcolor: "white", borderBottom: "1px solid #DAE0E2" }} gap="10px">
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#071F35" }}>
          Speed up the process by sharing key documents with potential buyers.
        </Typography>

        <Typography sx={{ fontSize: 16, fontWeight: 400, color: "#496682" }}>
          Sharing now helps us move quickly and attract serious interest. You can choose what to share.
        </Typography>

        <FormGroup>
          {Object.keys(sharedOptions).map((doc) => (
            <FormControlLabel
              key={doc}
              control={
                <Checkbox
                  checked={selectedShareOptions.includes(sharedOptions[doc])}
                  onChange={() => handleToggle(sharedOptions[doc])}
                />
              }
              label={doc}
            />
          ))}
        </FormGroup>
        <Stack direction="row" gap="10px" flexWrap="wrap" sx={{ maxHeight: "200px", overflow: "auto" }}>
          {selectedFiles.listOfNames.map((item) => (
            <Chip
              key={item}
              sx={{ padding: "4px" }}
              label={
                <Stack direction="row" alignItems="center">
                  <MaterialTooltip placement="top" title={item} isShow>
                    <Typography
                      sx={{
                        fontSize: "15px",
                        color: "#496682",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "100px"
                      }}
                    >
                      {item}
                    </Typography>
                  </MaterialTooltip>
                  <IconButton onClick={() => removeFile(item)} size="sm" sx={{ padding: 0 }}>
                    <CancelIcon />
                  </IconButton>
                </Stack>
              }
            />
          ))}
        </Stack>
        <Typography
          onClick={() => setFilesPickerModalOpen(true)}
          sx={{ fontSize: 15, fontWeight: 400, color: "#26A6AE", marginBottom: "10px", cursor: "pointer" }}
        >
          Add Files
        </Typography>
      </Stack>
      <FilesPickerModal
        filesPickerModalOpen={filesPickerModalOpen}
        onClose={() => setFilesPickerModalOpen(false)}
        currentFiles={[]}
        filesPickerSubmit={async ({ attachments, files }) => {
          filesPickerSubmit(attachments, files || []);
        }}
        title="Add Files"
        workspaceTitle="Select or Upload Files"
        dealId={dealId}
        getFilesOutsideDeal={true}
      />
      <Stack direction="row" justifyContent="flex-end" gap={2}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handlePlaceOnMarket} variant="contained">
          Select
        </Button>
      </Stack>
    </Stack>
  );
};

const style: StackProps["sx"] = {
  position: "absolute" as "absolute",
  display: "flex",
  flexDirection: "column",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  outline: "none",
  bgcolor: "white",
  p: "32px",
  gap: "20px",
  borderRadius: "8px"
};

//how to use
// const [isPlaceOnMarketModalOpen, setIsPlaceOnMarketModalOpen] = useState(false);
{
  /* <Modal open={isPlaceOnMarketModalOpen} onClose={() => setIsPlaceOnMarketModalOpen(false)}>
<PlaceOnMarketContent onClose={() => setIsPlaceOnMarketModalOpen(false)} />
</Modal> */
}
